import React, { useEffect, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { downloadFile, uploadFile } from "@utils/utilsApi";
import { ActionsType } from "src/interfaces/Common";
import { KeyURL, MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import ButtonBackPage from "@components/ButtonBackPage";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const Budgets: React.FC = () => {
  const [idBudgetDeleted, setidBudgetDeleted] = useState<string>();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openModalDelete, setOpenModalDelete] = useState(false);

  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["budget"]}`;

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getBudgets}`, //`${ServicesURL.getBadges}?corporateId=${id}`
      PagesID["budget"],
      filters[filterId],
    );

  const { t } = useTranslation(["budgets-page"]);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const { trigger: triggerDeleteBudget } = useSWRMutation(
    `${ServicesURL.getBudgets}/${idBudgetDeleted}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  const handleDelete = React.useCallback(async () => {
    if (idBudgetDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteBudget();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idBudgetDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setidBudgetDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idBudgetDeleted, mutate, triggerDeleteBudget]);

  useEffect(() => {
    if (!idBudgetDeleted) return;
  }, [idBudgetDeleted]);

  const dataFormat = useMemo(() => {
    return dataTable?.output?.map((el: any) => {
      if (el.cost_center != null && typeof el.cost_center === "object") {
        el = {
          ...el,
          cost_center: { ...el.cost_center, name: el.cost_center.sap_code },
        };
      }
      if (el.account != null && typeof el.account === "object") {
        el = {
          ...el,
          account: { ...el.account, name: el.account.sap_number },
        };
      }
      return el;
    });
  }, [dataTable?.output]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns.map((el) => {
        return { ...el };
      });
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => {
              setidBudgetDeleted(id);
              setOpenModalDelete(true);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    //FUNZIONE PER ABILITAZIONE/ DISABILITAZIONE CAMPI IN BASE A VALORI DELLA ROW
    configRowNew = configRowNew.map((el: ActionsType) => {
      if (el.type === "delete" && !row.original?.deletable) {
        return { ...el, disabled: true };
      }
      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "upload": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/download${
                    el ? `/${el}` : ""
                  }?corporate_id=1&type=LEARNER` || "",
                ),
              onUpload: (formData: FormData) => {
                return uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                );
              },
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        case "visibility": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${
                ServicesURL[action.service as KeyURL]
              }?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/?corporate_id=1` ||
                    "",
                ),
              onUpload: (formData: FormData) =>
                uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                ),
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-budget.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID.budget}
            data={dataFormat || []}
            columns={filteredColumns || []}
            configurationTopToolbar={actionsToolbar}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>

      <ModalConfirmsCst
        open={!!openModalDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalDelete(!openModalDelete);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
      />
    </AnimationFadeIn>
  );
};

export default Budgets;
